import { t } from "@lingui/macro";
import {
  type ProjectTimeBreakdownDetail,
  type ProjectTimeBreakdownResponse,
  UpdateProjectTimeBreakdownItemDocument,
  type UpdateProjectTimeBreakdownItemMutation,
  type UpdateProjectTimeBreakdownItemMutationVariables,
} from "@src/__generated__/graphql";
import type { TProgressBarSegment } from "@src/components/ui-kit/NewProgressBar";
import { MutationHelper } from "@src/helpers/apollo/mutation";
import type { AppStore } from "@src/stores/AppStore";
import {
  denominate,
  formatDate,
  HUNDRED_PERCENT,
  nominate,
} from "@src/utils/formatters";
import { required } from "@src/utils/forms/validators";
import { FieldState } from "formstate";
import { action, computed, makeObservable } from "mobx";

export class ProjectBreakdownGroupModel
  implements ProjectTimeBreakdownResponse
{
  id;
  title;
  tracked_time;
  tracked_amount;
  budgeted;
  budgeted_time;
  personal_expenses;
  profit;
  time_breakdown;

  constructor(init: ProjectTimeBreakdownResponse, appStore: AppStore) {
    makeObservable(this);
    this.id = init.id;
    this.title = init.title;
    this.tracked_time = init.tracked_time;
    this.tracked_amount = init.tracked_amount;
    this.budgeted = init.budgeted;
    this.budgeted_time = init.budgeted_time;
    this.personal_expenses = init.personal_expenses;
    this.profit = init.profit;
    this.time_breakdown = init.time_breakdown
      .filter(
        (item) =>
          (item.date_from && item.date_to) ||
          item.tracked_amount > 0 ||
          item.tracked_time > 0,
      )
      .map((item) => new ProjectBreakdownItemModel(item, appStore));
  }

  public static getProgressBarSegments(
    trackedAmount: number,
    budgeted: number,
  ) {
    const bars: TProgressBarSegment[] = [];

    if (budgeted === 0 && trackedAmount > 0) {
      bars.push({
        percent: HUNDRED_PERCENT,
        bg: "red.500",
      });
      return bars;
    }

    const percentage = (trackedAmount / budgeted) * HUNDRED_PERCENT;
    const over = percentage - HUNDRED_PERCENT;

    if (over > 0) {
      bars.push({
        percent: HUNDRED_PERCENT - over,
        bg: "teal.500",
      });
      bars.push({
        percent: over,
        bg: "red.500",
      });
    } else {
      bars.push({
        percent: percentage,
        bg: "teal.500",
      });
    }

    return bars;
  }

  @computed get canBeOpened() {
    return this.time_breakdown.some(
      ({ date_from, date_to }) => date_from && date_to,
    );
  }

  @computed get budgetDifference() {
    return (
      this.budgeted -
      this.time_breakdown.reduce((curr, { budgeted }) => curr + budgeted, 0)
    );
  }

  @computed get showBudgetWarning() {
    return this.canBeOpened && this.budgetDifference !== 0;
  }

  @computed get progressBarSegments(): TProgressBarSegment[] {
    return ProjectBreakdownGroupModel.getProgressBarSegments(
      this.tracked_amount,
      this.budgeted,
    );
  }
}

export class ProjectBreakdownItemModel implements ProjectTimeBreakdownDetail {
  id;
  tracked_time;
  tracked_amount;
  budgeted;
  budgeted_time;
  personal_expenses;
  profit;
  date_from;
  date_to;

  mutator = new MutationHelper<
    UpdateProjectTimeBreakdownItemMutation,
    UpdateProjectTimeBreakdownItemMutationVariables
  >(UpdateProjectTimeBreakdownItemDocument);

  budgetedField: FieldState<string>;

  constructor(
    init: ProjectTimeBreakdownDetail,
    private appStore: AppStore,
  ) {
    makeObservable(this);
    this.id = init.id;
    this.tracked_time = init.tracked_time;
    this.tracked_amount = init.tracked_amount;
    this.budgeted = init.budgeted;
    this.budgetedField = new FieldState(
      nominate(
        init.budgeted,
        this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ).toString(),
    ).validators(required);
    this.budgeted_time = init.budgeted_time;
    this.personal_expenses = init.personal_expenses;
    this.profit = init.profit;
    this.date_from = init.date_from;
    this.date_to = init.date_to;
  }

  @computed get title() {
    if (!this.date_from && this.date_to)
      return `${t`Before`} ${formatDate(new Date(this.date_to))}`;

    if (this.date_from && !this.date_to)
      return `${t`After`} ${formatDate(new Date(this.date_from))}`;

    if (!this.date_from || !this.date_to) return "";

    return this.date_from === this.date_to
      ? `${formatDate(new Date(this.date_from))}`
      : `${formatDate(new Date(this.date_from))} - ${formatDate(
          new Date(this.date_to),
        )}`;
  }

  @computed get progressBarSegments(): TProgressBarSegment[] {
    return ProjectBreakdownGroupModel.getProgressBarSegments(
      this.tracked_amount,
      this.budgeted,
    );
  }

  @action.bound async handleUpdateBudget(newBudget: number): Promise<boolean> {
    const [data, error] = await this.mutator.mutate({
      id: this.id,
      budgeted: denominate(
        newBudget,
        this.appStore.workspaceStore.settings?.currency.denomination ?? 2,
      ),
      dateFrom: this.date_from,
      dateTo: this.date_to,
    });

    if (error) return false;

    return Boolean(data.updateProjectTimeBreakdown.id);
  }
}
